import React, { FC, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import liveStreamIcon from "assets/svg/live-stream_white.svg";
import liveStreamBtnIcon from "assets/svg/live-stream-btn.svg";
import dashboardIcon from "assets/svg/dashboard.svg";
import userIcon from "assets/svg/user_blue.svg";
import settingIcon from "assets/svg/settings_1.svg";
import logoutIcon from "assets/svg/logout.svg";
import helpIcon from "assets/svg/chat-info.svg";
import superHostKey from "assets/svg/superhost-user-key-indicator-for-avatar.svg";
import closeIcon from "assets/svg/close-icon-in-modal.svg";
import chatExpandIcon from "assets/svg/chat-expand.svg";
import chatCollapseIcon from "assets/svg/chat-collapse.svg";
import Styles from "components/chatSideBar/ChatSideBar.module.scss";
import ChatParticipants from "./chatParticipants/ChatParticipants";
import ProfileAvatar from "components/profileAvatar/ProfileAvatar";
import { normalizeDistance, pickCanonicalPublicRoom } from "helpers/utils";
import { updateLastSeenMessage } from "store/services/firebase-broadcast";
import { callLogout } from "store/actions/auth.actions";
import { trackNavigation, trackModalInteraction } from "utils/clarity";

interface ChatSideBarProps {
  cssClass?: string;
  conversations: any;
  setSelected: Function;
  activateRoom?: any;
  setActiveRoom?: any;
  callBack?: any;
  selected?: any;
  sidebarOpen?: boolean;
  onClose?: () => void;
  onRoomChange?: () => void;
  onCreateRoom?: () => void;
  showAppNav?: boolean;
  /** Already on the full-screen chat page, so no expand affordance. */
  fullPage?: boolean;
}

const user = JSON.parse(localStorage.getItem("user") as string);
const ChatSideBar: FC<ChatSideBarProps> = (props: ChatSideBarProps) => {
  const {
    cssClass,
    conversations,
    setSelected,
    activateRoom,
    setActiveRoom,
    callBack,
    selected,
    sidebarOpen,
    onClose,
    onRoomChange,
    onCreateRoom,
    showAppNav = false,
    fullPage = false,
  } = props;
  const [selectedChat, setSelectedChat] = useState(-1);
  const [appNavOpen, setAppNavOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const closeSidebar = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!sidebarOpen) {
      setAppNavOpen(false);
    }
  }, [sidebarOpen]);

  const handleLiveStreamClick = () => {
    history.push('/conversations-nearby');
    closeSidebar();
  };

  const handleCreateRoom = () => {
    trackModalInteraction("create_room", "opened");
    closeSidebar();
    if (onCreateRoom) {
      onCreateRoom();
    }
  };

  // Dashboard → full-screen chat page. Carry the open room through router state so
  // the full page keeps the same conversation instead of auto-picking the first one.
  const handleExpandChat = () => {
    trackNavigation("/chat-room", "chat_sidebar_header");
    closeSidebar();
    history.push("/chat-room", selected?.pk ? { selected, preserveSelection: true } : undefined);
  };

  // Full-screen chat page → back to the Dashboard chat section, keeping the room.
  const handleCollapseChat = () => {
    trackNavigation("/dashboard", "chat_sidebar_header");
    closeSidebar();
    history.push("/dashboard", selected?.pk ? { selected, preserveSelection: true } : undefined);
  };

  const handlePublicProfile = () => {
    trackNavigation("/profile", "chat_sidebar");
    closeSidebar();
    history.push("/profile");
  };

  const handleLogOut = () => {
    closeSidebar();
    dispatch(callLogout());
    history.push("/login");
  };
  
  const handleChangeChat = (i: any) => {
    if (!i || !i.pk) {
      console.warn('Invalid item passed to handleChangeChat:', i);
      return;
    }

    // Re-selecting the room you're already in would replace `selected` with a fresh
    // object that has no loaded media/attachments, blanking the Chat Info Photos &
    // Media (its load effect is keyed on pk, so it wouldn't re-fetch). Skip the
    // reselect so the already-loaded media survives; still run the click side effects.
    if (i.pk === selected?.pk) {
      if (onRoomChange) {
        onRoomChange();
      }
      if (onClose) {
        onClose();
      }
      updateLastSeenMessage(i.pk, user.pk);
      return;
    }

    callBack(i)
    setSelectedChat(i.pk);
    setSelected(i);
    
    // Close ChatInfo drawer when switching rooms
    if (onRoomChange) {
      onRoomChange();
    }
    
    // Close mobile sidebar when a room is selected
    if (onClose) {
      onClose();
    }
    
    // Update room status
    // let updateRoom = {
    //   room:i,
    //   activeRoom: moment().format()
    // };
    // updateNewRoomStatus(updateRoom)
    
    // Update lastSeenMessage to mark messages as read
    updateLastSeenMessage(i.pk, user.pk);
  };

  useEffect(() => {
    if (selected && selected.pk) {
      setSelectedChat(selected.pk);
    }
  }, [selected])

  const sortRooms = (rooms: any) => {
    const welcomeRoom = pickCanonicalPublicRoom(conversations);
    const welcomePk = welcomeRoom ? Number(welcomeRoom.pk) : null;

    return rooms.sort((a: any, b: any) => {
      // Priority 0: Canonical Welcome room stays at the top of the list
      const isWelcomeA = welcomePk != null && Number(a.pk) === welcomePk;
      const isWelcomeB = welcomePk != null && Number(b.pk) === welcomePk;
      if (isWelcomeA && !isWelcomeB) return -1;
      if (!isWelcomeA && isWelcomeB) return 1;

      // Priority 1: Rooms hosted by the user
      const isHostA = a.host?.pk === user?.pk;
      const isHostB = b.host?.pk === user?.pk;

      if (isHostA && !isHostB) return -1;
      if (!isHostA && isHostB) return 1;

      // Priority 2: Rooms where user is a member
      const isMemberA = a.participants?.some((p: any) => p.pk === user?.pk);
      const isMemberB = b.participants?.some((p: any) => p.pk === user?.pk);

      if (isMemberA && !isMemberB) return -1;
      if (!isMemberA && isMemberB) return 1;

      // Priority 3: Sort by distance, handle rooms without location
      const distanceA = normalizeDistance(a.distance);
      const distanceB = normalizeDistance(b.distance);

      if (distanceA !== distanceB) {
        return distanceA - distanceB;
      }

      // Priority 4: If distances are equal (or both invalid), sort by creation time
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  };

  const videoRooms = conversations?.length > 0 ?
    sortRooms(conversations.filter((r: any) => r.host && r.call_active === true && r.is_deleted !== true))
    : [];

  const chatRooms = conversations?.length > 0 ?
    sortRooms(conversations.filter((r: any) => r.host && r.call_active !== true && r.is_deleted !== true))
    : [];
    
  return (
    <aside className={`${cssClass || ''} ${sidebarOpen ? Styles.sidebarOpen : ''}`}>
      <div
        className={`${Styles.nearbyRooms} ${showAppNav ? Styles.hideChatWhenAppNav : ''}`}
      >
        {fullPage ? (
          <button
            type="button"
            className={Styles.chatHeaderExpand}
            onClick={handleCollapseChat}
            aria-label="Back to dashboard chat"
          >
            <h6 className="m-0 font-weight-600">Chat</h6>
            <img src={chatCollapseIcon} alt="" className={Styles.chatHeaderExpandIcon} />
          </button>
        ) : (
          <button
            type="button"
            className={Styles.chatHeaderExpand}
            onClick={handleExpandChat}
            aria-label="Open chat in full screen"
          >
            <h6 className="m-0 font-weight-600">Chat</h6>
            <img src={chatExpandIcon} alt="" className={Styles.chatHeaderExpandIcon} />
          </button>
        )}
      </div>

      {/* Phone-only on /chat-room: account toggle + expandable app nav */}
      {showAppNav && (
        <nav className={`${Styles.appNav} ${appNavOpen ? Styles.appNavExpanded : ''}`} aria-label="App">
          <div className={Styles.appNavHeader}>
            <button
              type="button"
              className={Styles.appNavToggle}
              onClick={() => setAppNavOpen((open) => !open)}
              aria-expanded={appNavOpen}
              aria-controls="chat-sidebar-app-nav"
            >
              <span className={Styles.appNavAvatarWrap}>
                <ProfileAvatar
                  profile={user?.profile}
                  alt=""
                  className={Styles.appNavAvatar}
                />
                {user?.is_super_host && (
                  <img src={superHostKey} alt="" className={Styles.appNavSuperHostKey} />
                )}
              </span>
              <span className={Styles.appNavUserName}>{user?.name || 'User'}</span>
              <i
                className={`fas fa-angle-down ${Styles.appNavChevron}`}
                aria-hidden="true"
              />
            </button>
            {onClose && (
              <button
                type="button"
                className={Styles.appNavClose}
                onClick={onClose}
                aria-label="Close sidebar"
              >
                <img src={closeIcon} alt="" className={Styles.appNavCloseIcon} />
              </button>
            )}
          </div>
          <div
            id="chat-sidebar-app-nav"
            className={Styles.appNavPanel}
            aria-hidden={!appNavOpen}
          >
            <ul
              className={`${Styles.appNavList} list-unstyled mb-0`}
            >
              <li>
                <Link
                  className={Styles.appNavItem}
                  to="/dashboard"
                  onClick={() => {
                    trackNavigation("/dashboard", "chat_sidebar");
                    closeSidebar();
                  }}
                >
                  <img src={dashboardIcon} alt="" />
                  <span>Dashboard</span>
                </Link>
              </li>
              <li>
                <button type="button" className={Styles.appNavItem} onClick={handleCreateRoom}>
                  <i className={`fas fa-plus ${Styles.appNavCreateIcon}`} aria-hidden="true" />
                  <span>Create Room</span>
                </button>
              </li>
              <li>
                <button type="button" className={Styles.appNavItem} onClick={handlePublicProfile}>
                  <img src={userIcon} alt="" className={Styles.appNavProfileIcon} />
                  <span>View Public Profile</span>
                </button>
              </li>
              <li>
                <Link
                  className={Styles.appNavItem}
                  to="/profile/edit"
                  onClick={() => {
                    trackNavigation("/profile/edit", "chat_sidebar");
                    closeSidebar();
                  }}
                >
                  <img src={settingIcon} alt="" className={Styles.appNavSettingsIcon} />
                  <span>Settings</span>
                </Link>
              </li>
              <li>
                <Link
                  className={Styles.appNavItem}
                  to="/help"
                  onClick={() => {
                    trackNavigation("/help", "chat_sidebar");
                    closeSidebar();
                  }}
                >
                  <img src={helpIcon} alt="" className={Styles.appNavHelpIcon} />
                  <span>Help</span>
                </Link>
              </li>
              <li>
                <button type="button" className={Styles.appNavItem} onClick={handleLogOut}>
                  <img src={logoutIcon} alt="" />
                  <span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      )}

      <div className={`${Styles.nearbyRoomsListing} customScrollBarMuted`}>
        <p className={`${Styles.status} ${Styles.success} text-uppercase mb-0`}>
          LIVE
        </p>
        <ul className={`${Styles.activeListing} customScrollBarMuted list-unstyled`}>
          {videoRooms.map((i: any, index: number) => (
            <ChatParticipants
              item={i}
              key={i.pk || index}
              handleChangeIndex={handleChangeChat}
              itemIndex={i.pk === selectedChat}
              isSuperHost={i.host?.is_super_host}
              id={i.pk}
              activateRoom={activateRoom}
              setActiveRoom={setActiveRoom}
              callBack={callBack}
              selected={selected}
            />
          ))}
        </ul>
        <p className={`${Styles.nearbyLabel} ${Styles.status} text-uppercase mb-0`}>Nearby Rooms</p>
        <ul className={`${
            videoRooms?.length < 1
              ? Styles.offlineListingFull
              : videoRooms?.length === 1
              ? Styles.offlineListingOne
              : videoRooms?.length === 2
              ? Styles.offlineListingTwo
              : videoRooms?.length === 3
              ? Styles.offlineListingThree
              : Styles.offlineListing
          } customScrollBarMuted list-unstyled mb-0`}>
          {chatRooms.map((i: any, index: number) => (
            <ChatParticipants
              item={i}
              key={i.pk || index}
              handleChangeIndex={handleChangeChat}
              itemIndex={i.pk === selectedChat}
              isSuperHost={i.host?.is_super_host}
              id={i.pk}
              offLineImageCss
              selected={selected}
            />
          ))}
        </ul>
      </div>

      {/* Live Stream banner */}
      <div
        className={Styles.liveStream}
        onClick={handleLiveStreamClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLiveStreamClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Go to Live Stream"
      >
        <div className={Styles.liveStreamInfo}>
          <div className={Styles.liveStreamIcon}>
            <img src={liveStreamIcon} alt="" className={Styles.liveStreamIconImg} />
          </div>
          <div>
            <p className={Styles.liveStreamTitle}>LIVE STREAM</p>
            <p className={Styles.liveStreamSub}>Watch Live &amp; Join Events</p>
          </div>
        </div>
        <span className={Styles.liveStreamBtn} aria-hidden="true">
          <img src={liveStreamBtnIcon} alt="" className={Styles.liveStreamBtnImg} />
        </span>
      </div>

      {/* Floating close — Dashboard phone drawer (no account row) */}
      {onClose && !showAppNav && (
        <button
          className={Styles.mobileCloseButton}
          onClick={onClose}
          aria-label="Close sidebar"
          type="button"
        >
          <img src={closeIcon} alt="" width={20} height={20} />
        </button>
      )}
    </aside>
  );
};
export default ChatSideBar;
